<template>
  <div id="app">
    <div :class='$store.state.loadEvents ? " wait" : ""'>
      <div
        v-if="$store.state.loadEvents"
        class="text-center justify-content-center  align-items-center"
      >
        <div
          class="spinner-border align-items-center "
          role="status"
          style="width: 4rem; height: 4rem;"
        >
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
    <transition
      mode="out-in"
      name="fade"
    >
      <router-view />
    </transition>
  </div>
</template>

<script>
// ResizeObserver
// A polyfill for the Resize Observer API
// https://github.com/que-etc/resize-observer-polyfill
import ResizeObserver from 'resize-observer-polyfill'

// Hex To RGBA
// hex-to-rgba turns an old-fashioned css hex color value string into an rgba() string.
// https://github.com/misund/hex-to-rgba
import hexToRgba from 'hex-to-rgba'

// Vuex
// https://vuex.vuejs.org/guide/state.html#the-mapstate-helper
import { mapState } from 'vuex'
import { componentType } from '@/enums/constantes'
import { getTypeByComponent } from '@/utils'

export default {
  name: 'App',
  computed: {
    // The mapState Helper
    // https://vuex.vuejs.org/guide/state.html#the-mapstate-helper
    ...mapState({
      config: state => state.config,
      loading: state => state.loading,
      loadEvents: state => state.loadEvents,
      display: state => state.formJson.display,
      components: state => state.formJson.components,
      waitRenderForm: state => state.config.waitRenderForm
    })
  },
  methods: {
    async actionRenderForm (payload) {
      if (payload.data.jsonData) {
        await this.$store.dispatch('setJsonInjetado', payload.data.jsonData)
      }
      this.render(this.display)
    },
    /**
     * Aguarda receber um postMessage para uma solicitação de connect de origem formBuilderSDK
     * @param  {Object} payload Objeto da mensagem recebida
     */
    receiveMessage (payload) {
      if (payload.origin === 'formBuilderSDK') {
        if (payload.type === 'command') {
          if (payload.command === 'initialize') {
            this.$store.dispatch('initialize', payload.data)

            // Observa a altura do app e informa ao SDK sempre que houver alteração
            const resize = new ResizeObserver((entries, observer) => {
              setTimeout(() => this.updateHeight(), 500)
            })
            resize.observe(document.body)

            // Verifica se possui os parametros corretos
            if (payload.data.colors) this.setVueStyle(payload.data.colors)

            switch (payload.action) {
              case 'form':
              case 'static':
                // Rotina...
                break
            }
          } else if (payload.action === 'renderForm') {
            this.actionRenderForm(payload)
          } else if (payload.action === 'getKeyCustomFields') {
            this.getKeyCustomFields()
          } else if (payload.action === 'showOnly') {
            this.$store.dispatch('setShowOnly', payload.data)
          }
        }
      }
    },
    getKeyCustomFields () {
      const keyCustoms = []

      const extractCustomKey = (component) => {
        if (component.keyCustom) {
          return {
            keyCustom: component.keyCustom,
            injectedJson: component.data.injectedJson,
            key: component.key,
            type: getTypeByComponent(component),
            typeComponent: component.type
          }
        }
      }

      this.components.forEach((component) => {
        if (component.type === componentType.group) {
          component.components.forEach((componentInGroup) => {
            if (componentInGroup.keyCustom) {
              keyCustoms.push(extractCustomKey(componentInGroup))
            }
          })
        } else {
          if (component.keyCustom) {
            keyCustoms.push(extractCustomKey(component))
          }
        }
      })

      const payload = {
        origin: 'formBuilderWebRender',
        client: this.config.identifier,
        type: 'message',
        message: 'getKeyCustomFields',
        data: {
          keyCustoms
        },
        timestamp: Date.now()
      }
      window.parent.postMessage(payload, '*')
    },
    /**
     * Atualiza as cores do app
     * @param  {Object} colors Cores do configurador do cliente
     */
    setVueStyle (colors) {
      if (colors.primary) {
        const color = colors.primary
        this.$style['brand-primary'] = color
        this.$style['input-bg'] = hexToRgba(color, '0.08')
        this.$style['input-focus-bg'] = hexToRgba(color, '0.16')
        this.$style['input-file-read-only-card-bg'] = hexToRgba(color, '0.05')
        this.$style['input-border'] = hexToRgba(color, '0.08')
        this.$style['input-border-focus'] = hexToRgba(color, '0.08')
        this.$style['state-focus-border-bottom'] = hexToRgba(color, '0.08')
        this.$style['state-focus-placeholder'] = hexToRgba(color, '0.3')
      }
      if (colors.errors) {
        const color = colors.errors
        this.$style['brand-danger'] = color
        this.$style['state-danger-text'] = color
        this.$style['state-danger-bg'] = hexToRgba(color, '0.08')
        this.$style['state-danger-focus-bg'] = hexToRgba(color, '0.16')
        this.$style['state-danger-border'] = hexToRgba(color, '0.08')
        this.$style['state-danger-addon-text'] = hexToRgba(color, '0.4')
        this.$style['state-danger-placeholder'] = hexToRgba(color, '0.3')
      }
      if (colors.alert) {
        const color = colors.alert
        this.$style['brand-warning'] = color
        this.$style['state-warning-text'] = color
        this.$style['state-warning-bg'] = hexToRgba(color, '0.08')
        this.$style['state-warning-focus-bg'] = hexToRgba(color, '0.16')
        this.$style['state-warning-border'] = hexToRgba(color, '0.08')
        this.$style['state-warning-addon-text'] = hexToRgba(color, '0.4')
        this.$style['state-warning-placeholder'] = hexToRgba(color, '0.3')
      }
    },
    /**
     * Envia ao SDK a altura do conteudo do iframe
     */
    updateHeight () {
      const milliseconds = new Date().getTime()
      const config = this.$store.getters.config
      let height = this.$root.$el.scrollHeight
      const payload = {
        origin: 'formBuilderWebRender',
        client: config.identifier,
        type: 'command',
        command: 'set',
        action: 'height',
        data: {
          height: height
        },
        timestamp: milliseconds
      }
      window.parent.postMessage(payload, '*')
    },

    render (pageName) {
      switch (pageName) {
        case 'form':
        case 'wizard':
          if (this.$route.path.indexOf('playground') !== -1) {
            pageName += '-playground'
          }
          this.$router.replace({
            name: pageName,
            params: {
              components: this.components
            }
          })
          break
      }
    }
  },
  watch: {
    loading (after) {
      const milliseconds = new Date().getTime()
      const config = this.$store.getters.config
      const payload = {
        origin: 'formBuilderWebRender',
        client: config.identifier,
        type: 'message',
        message: 'LOADING',
        data: after,
        timestamp: milliseconds
      }
      window.parent.postMessage(payload, '*')
    },
    display (after) {
      if (!this.waitRenderForm) {
        this.render(after)
      }
    }
  },
  created () {
    // Observa mensagens do SDK
    this.$bindEvent(window, 'message', (event) => this.receiveMessage(event.data))
  }
}
</script>

<style scoped>

.wait {
  display: inline-block;
  width: 100%;
  height: 100%;
  margin-top: 300px;
  opacity: 0.65;
  z-index: 10;
  position: fixed;
  left: 0;
  top: 0;
}
</style>
